import React, { useEffect } from "react";
import { Button, ButtonProps } from "antd";
import {
  DownloadOutlined,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useReportDownload } from "../../../../hooks/useReportDownload";
import {
  logoToBase64,
  getTenantFromToken,
  getUserFromToken,
} from "../../../../utils/utils";
import Logo from "../../../../assets/image.png";
import jope from "../../../../assets/jope.png";
import Mightier from "../../../../assets/Mightier_logo.png";

const CLIENT_LOGOS: Record<string, string> = {
  "7563c07c-2572-4cc2-b14b-1d43b6010a78": jope,
  "00000000-0000-0000-0000-000000000000": Logo,
  "544189b1-c029-4dd6-b651-15207fccfe76": Mightier,
};

interface DownloadReportButtonProps {
  job_config_id?: string;
  jobType?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  buttonProps?: Omit<ButtonProps, "onClick" | "disabled" | "icon">;
  showStatusText?: boolean;
}

const DownloadReportButton: React.FC<DownloadReportButtonProps> = ({
  job_config_id,
  wrapperClassName = "",
  buttonClassName = "",
  buttonProps,
  showStatusText = true,
}) => {
  const { status, error, downloadReport, reset } = useReportDownload();

  const isActive = status === "pending" || status === "processing";
  const isError = status === "error";
  const isDisabled = isActive;

  // 🔥 Auto reset after success
  useEffect(() => {
    if (status === "done") {
      const timer = setTimeout(() => {
        reset();
      }, 1500); // small delay so user sees completion briefly

      return () => clearTimeout(timer);
    }
  }, [status, reset]);

  const handleClick = async () => {
    if (isDisabled) return;

    const tenant_id = getTenantFromToken() ?? "";
    const user_id = getUserFromToken() ?? "";

    let logo_b64: string | undefined;

    try {
      const logoSrc = CLIENT_LOGOS[tenant_id];
      if (logoSrc) {
        logo_b64 = await logoToBase64(logoSrc);
      }
    } catch (e) {
      console.warn("Logo fetch failed, backend will use initials fallback", e);
    }

    downloadReport({
      logo_b64,
      job_config_id,
      user_id,
    });
  };

  const labelMap: Record<string, string> = {
    idle: "Download Report",
    pending: "Starting...",
    processing: "Generating PDF...",
    done: "Download Report",
    error: "Retry",
  };

  const iconMap: Record<string, React.ReactNode> = {
    idle: <DownloadOutlined />,
    pending: <LoadingOutlined spin />,
    processing: <LoadingOutlined spin />,
    done: <DownloadOutlined />,
    error: <WarningOutlined />,
  };

  return (
    <div className={`download-report-wrapper ${wrapperClassName}`}>
      <Button
        type="primary"
        size="small"
        {...buttonProps}
        icon={iconMap[status]}
        onClick={handleClick}
        disabled={isDisabled}
        className={`download-report-btn ${buttonClassName}`}
      >
        {labelMap[status]}
      </Button>

      {showStatusText && isActive && (
        <p className="download-report-hint">
          This may take a few seconds…
        </p>
      )}

      {showStatusText && isError && error && (
        <p
          className="download-report-error"
          onClick={reset}
          role="button"
        >
          {error} · Click to retry
        </p>
      )}

      <style>{`
        .download-report-wrapper {
          display: inline-flex;
          flex-direction: column;
          gap: 4px;
        }

        .download-report-hint,
        .download-report-error {
          font-size: 11px;
          margin: 0;
          white-space: nowrap;
        }

        .download-report-hint {
          color: #6B7280;
        }

        .download-report-error {
          color: #dc2626;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default DownloadReportButton;